import { Shift } from 'app/modules/components/routing/sub-routes/route-optimization/route-optimization.interface';

/**
 * Los **parámetros** con los que se planificó una ruta, y de dónde salió cada uno.
 *
 * Por qué existe: `resolveTerritoryFleet` los arma con su procedencia
 * (`capacitySource`, `shiftSource`, `tripsSource`), se los manda a `POST /plan` y los
 * descarta. Sin ellos, dos rutas que se ven idénticas pueden haberse armado con
 * supuestos distintos y no hay forma de saber cuál mirar — y una capacidad que el
 * maestro no declara se muestra igual que una real.
 *
 * Nombrar los insumos NO es reconstruir el motivo. El contrato del optimizador no
 * informa por qué prefirió una secuencia; lo que sí se puede afirmar es qué entró a
 * la petición, y eso es verificable contra lo que se envió.
 *
 * Módulo puro: entran los parámetros, salen sus lecturas. Sin HTTP, sin Angular.
 */

/** De dónde salió un parámetro. */
export type ParamSource = 'master' | 'fallback';

/** Rótulo de cada procedencia, tal como se lee en el panel. */
export const PARAM_SOURCE_LABELS: Readonly<Record<ParamSource, string>> = {
  master: 'dato cargado',
  fallback: 'supuesto',
};

export interface RoutePlanParams {
  /** Camión de la ruta, como lo rotula la tarjeta. */
  vehicleLabel?: string;
  capacityLb: number;
  capacitySource: ParamSource;
  shift: Shift;
  shiftSource: ParamSource;
  /** Techo de viajes del turno. `1` = sin recargas. */
  maxTrips: number;
  /** Minutos de carga en el almacén entre viajes. */
  reloadMin: number;
  tripsSource: ParamSource;
  /** Minutos de atención por parada. Nunca sale del maestro. */
  serviceMin: number;
  /** Ventana de entrega del territorio, y solo si es inequívoca. */
  window?: Shift;
  /** Camiones que servían el territorio. */
  fleetSize: number;
  territoryName?: string;
}

/** Una fila del panel de parámetros. */
export interface ParamRow {
  label: string;
  value: string;
  /**
   * De dónde salió. **Ausente** en las filas que no son un parámetro supuesto —el
   * camión y el tamaño de la flota son hechos del maestro, no elecciones—: marcarlas
   * con una procedencia insinuaría que podrían haber sido otra cosa.
   */
  source?: ParamSource;
  /** Por qué el valor está ausente. Solo cuando lo está. */
  note?: string;
}

/** Miles con coma, el mismo formato que la barra de capacidad de la tarjeta. */
function lb(value: number): string {
  return `${Math.round(value).toLocaleString('en-US')} lb`;
}

/** `08:00–17:00`. Con raya, que es un rango y no una resta. */
function range(shift: Shift | undefined): string {
  return shift?.start && shift?.end ? `${shift.start}–${shift.end}` : '';
}

/** `true` si la capacidad con la que se planificó no salió del vehículo del maestro. */
export function capacityAssumed(params: RoutePlanParams | undefined): boolean {
  // De la PROCEDENCIA, no de comparar contra el valor por defecto: un vehículo que
  // declare exactamente esa cifra es un dato real, y compararlo daría un falso
  // positivo permanente sobre una flota legítima.
  return params?.capacitySource === 'fallback';
}

/**
 * La lectura compacta, para el recorrido de una parada.
 *
 * Enumera los insumos y **no nombra a nadie**: el sujeto lo pone quien la usa, que
 * es el tramo de ubicación. Así la misma cadena sirve donde el sujeto es otro.
 */
export function paramsSummary(params: RoutePlanParams | undefined): string {
  if (!params) return '';

  const partes: string[] = [
    `capacidad ${lb(params.capacityLb)}${capacityAssumed(params) ? ' (supuesta)' : ''}`,
  ];
  const jornada = range(params.shift);
  if (jornada) partes.push(`jornada ${jornada}`);
  partes.push(`${params.serviceMin} min de atención por parada`);
  // Un solo viaje es el caso normal y no agrega nada; las recargas SÍ, porque
  // cambian cuánto puede cargar el camión en el día.
  if (params.maxTrips > 1) partes.push(`hasta ${params.maxTrips} viajes`);
  const ventana = range(params.window);
  if (ventana) partes.push(`ventana ${ventana}`);

  return partes.join(' · ');
}

/** Las filas del panel: un parámetro por fila, con su procedencia. */
export function paramRows(params: RoutePlanParams): ParamRow[] {
  const rows: ParamRow[] = [];

  if (params.vehicleLabel) rows.push({ label: 'Camión', value: params.vehicleLabel });

  rows.push({
    label: 'Capacidad',
    value: lb(params.capacityLb),
    source: params.capacitySource,
  });
  rows.push({
    label: 'Jornada',
    value: range(params.shift) || '—',
    source: params.shiftSource,
  });
  rows.push({
    label: 'Viajes',
    value:
      params.maxTrips > 1
        ? `hasta ${params.maxTrips}, con ${params.reloadMin} min de carga entre uno y otro`
        : '1, sin recargas',
    source: params.tripsSource,
  });
  rows.push({
    // Nunca sale del maestro: es un supuesto de pantalla, y presentarlo como
    // declarado sería exactamente la mentira que este panel viene a evitar.
    label: 'Atención',
    value: `${params.serviceMin} min por parada`,
    source: 'fallback',
  });

  const ventana = range(params.window);
  rows.push(
    ventana
      ? { label: 'Ventana', value: ventana }
      : {
          label: 'Ventana',
          value: '—',
          // No se rellena con la jornada: la ventana se aplica a los PEDIDOS y la
          // jornada al camión. Igualarlas afirmaría una restricción que no se envió.
          note: 'el territorio no declara una sola ventana de entrega',
        },
  );

  rows.push({
    label: 'Camiones del territorio',
    value:
      params.fleetSize === 1
        ? `1 camión${params.territoryName ? ' en ' + params.territoryName : ''} — no hubo alternativa`
        : `${params.fleetSize} camiones${params.territoryName ? ' en ' + params.territoryName : ''}`,
  });

  return rows;
}
